import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FaFutbol, FaUsers, FaTrophy, FaChalkboardTeacher, FaCalendarAlt, FaClock, FaUserGraduate, FaShieldAlt, FaTools, FaLaptopCode, FaWrench, FaBolt, FaCar, FaCamera, FaPencilRuler, FaHardHat, FaSchool, FaGamepad, FaRunning } from 'react-icons/fa';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import bgImage from '../../assets/img/pictures/tttt.jpg';

const Programs = () => {
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  const ComingSoonModal = () => (
    <AnimatePresence>
      {showModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => setShowModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaClock className="text-2xl text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Coming Soon!</h3>
              <p className="text-gray-600 mb-6">This feature is currently under development. Stay tuned for updates!</p>
              <button
                onClick={() => setShowModal(false)}
                className="bg-blue-600 text-white px-6 py-2 rounded-full font-semibold hover:bg-blue-700 transition-colors duration-200"
              >
                Got it
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const coachingPrograms = [
    {
      stage: "Foundation Stage",
      ageRange: "U9 - U11",
      hours: "3-5 hours per week",
      description: "Focus on fundamental skills and love for the game",
      icon: <FaFutbol />,
      color: "blue"
    },
    {
      stage: "Youth Development Stage",
      ageRange: "U12 - U16",
      hours: "6-12 hours per week",
      description: "Technical development and tactical awareness",
      icon: <FaRunning />,
      color: "green"
    },
    {
      stage: "Professional Development Stage",
      ageRange: "U17 - U21",
      hours: "12-14 hours per week",
      description: "Advanced training for professional preparation",
      icon: <FaTrophy />,
      color: "red"
    }
  ];

  const gamesPrograms = [
    {
      stage: "Foundation Stage",
      ageRange: "U9 - U11",
      description: "Local games on Sundays, regular festivals with 4+ clubs",
      features: [
        "Age-appropriate pitch sizes",
        "Small-sided match games",
        "50% match playing time per season",
        "Focus on fun and ball mastery"
      ],
      icon: <FaGamepad />,
      color: "blue"
    },
    {
      stage: "Youth Development Stage",
      ageRange: "U12 - U16",
      description: "Weekend games and seasonal festivals",
      features: [
        "50% match playing time for U12-U14",
        "20+ authorized games for U15-U16",
        "Tournament football introduction",
        "Technical and tactical development"
      ],
      icon: <FaRunning />,
      color: "green"
    },
    {
      stage: "Professional Development Stage",
      ageRange: "U17 - U21",
      description: "Preparation for professional football",
      features: [
        "U18 League games on Saturdays",
        "Professional squad integration",
        "Tournament participation",
        "Domestic and international exposure"
      ],
      icon: <FaTrophy />,
      color: "red"
    }
  ];

  const educationPrograms = [
    {
      phase: "Foundation Phase",
      ageRange: "U9 - U11",
      description: "Full-time academic education with evening sports activities",
      features: [
        "Regular school attendance at Kempshot Grammar Academy",
        "Evening sports activities",
        "Weekend and holiday training",
        "Balanced academic-sports schedule"
      ],
      icon: <FaSchool />,
      color: "blue"
    },
    {
      phase: "Youth Development Phase",
      ageRange: "U12 - U16",
      description: "Flexible education options with sports integration",
      options: [
        {
          title: "Part-time",
          description: "Full-time school with evening sports activities"
        },
        {
          title: "Full-time",
          description: "Residential academy program with scholarship opportunities"
        },
        {
          title: "Hybrid",
          description: "Flexible school release for academy activities"
        }
      ],
      icon: <FaUserGraduate />,
      color: "green"
    },
    {
      phase: "Professional Development Phase",
      ageRange: "U17 - U21",
      description: "Professional preparation with educational support",
      features: [
        "Professional player development",
        "Educational apprenticeship program",
        "12-week educational reviews",
        "Optional degree-level study opportunities"
      ],
      icon: <FaTrophy />,
      color: "red"
    }
  ];

  return (
    <div className="min-h-screen relative">
      <ComingSoonModal />
      {/* Background Image */}
      <div className="fixed inset-0 z-0">
        <img
          src={bgImage}
          alt="Background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/95 via-blue-900/90 to-black/95"></div>
      </div>

      {/* Content */}
      <div className="relative z-10">
        <Header />

        {/* Main Container Card */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Section */}
          <section className="relative py-8 sm:py-12 md:py-20">
            <div className="container mx-auto">
              <div className="flex flex-col lg:flex-row items-center gap-6 sm:gap-8 lg:gap-12">
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                  className="lg:w-1/2 text-center lg:text-left w-full"
                >
                  <h1 className="text-2xl xs:text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 sm:mb-4 md:mb-6">
                    Comprehensive Development Programs
                  </h1>
                  <p className="text-base sm:text-lg md:text-xl text-blue-100 mb-6 sm:mb-8">
                    Our integrated approach combines professional coaching, competitive games, 
                    and quality education to develop well-rounded athletes.
                  </p>
                  <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setShowModal(true)}
                      className="bg-blue-600 text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-full font-semibold text-sm sm:text-base md:text-lg hover:bg-blue-700 transition-colors duration-200 w-full sm:w-auto"
                    >
                      View Programs
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setShowModal(true)}
                      className="bg-white/10 text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-full font-semibold text-sm sm:text-base md:text-lg hover:bg-white/20 transition-colors duration-200 w-full sm:w-auto"
                    >
                      Download Guide
                    </motion.button>
                  </div>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                  className="lg:w-1/2 w-full mt-8 lg:mt-0"
                >
                  <div className="relative max-w-[500px] mx-auto">
                    <img
                      src={require("../../assets/img/pictures/WhatsApp Image 2025-02-27 at 21.45.52_fdc9a380.jpg")}
                      alt="Elite Academy Training"
                      className="rounded-2xl shadow-2xl w-full h-[300px] sm:h-[350px] md:h-[450px] object-cover"
                    />
                    <motion.div 
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                      className="absolute -bottom-4 sm:-bottom-6 -right-4 sm:-right-6 bg-blue-600 text-white p-3 sm:p-4 rounded-lg shadow-lg"
                    >
                      <p className="text-sm sm:text-base font-semibold">Integrated Approach</p>
                      <p className="text-xs sm:text-sm opacity-80">Sports & Education</p>
                    </motion.div>
                  </div>
                </motion.div>
              </div>
            </div>
          </section>

          {/* Coaching Program Section */}
          <section className="py-8 sm:py-12 md:py-16">
            <div className="max-w-7xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center mb-8 sm:mb-12"
              >
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4">Coaching Program</h2>
                <p className="text-base sm:text-lg md:text-xl text-blue-100 max-w-2xl mx-auto">
                  Our coaching program is closely integrated with education and welfare, 
                  ensuring a balanced development approach for every player.
                </p>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
                {coachingPrograms.map((program, index) => (
                  <motion.div
                    key={program.stage}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6"
                  >
                    <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                      <div className={`text-2xl sm:text-3xl text-${program.color}-400`}>
                        {program.icon}
                      </div>
                      <div>
                        <h3 className="text-lg sm:text-xl font-bold text-white">{program.stage}</h3>
                        <p className="text-sm sm:text-base text-blue-200">{program.ageRange}</p>
                      </div>
                    </div>
                    <p className="text-sm sm:text-base text-blue-100 mb-3 sm:mb-4">{program.description}</p>
                    <div className="text-sm sm:text-base text-white/80">
                      <p className="font-semibold">Training Hours:</p>
                      <p>{program.hours}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Games Program Section */}
          <section className="py-8 sm:py-12 md:py-16 bg-white/5 backdrop-blur-sm">
            <div className="max-w-7xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center mb-8 sm:mb-12"
              >
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4">Games Program</h2>
                <p className="text-base sm:text-lg md:text-xl text-blue-100 max-w-2xl mx-auto">
                  Structured games program with safety-first approach across all levels
                </p>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
                {gamesPrograms.map((program, index) => (
                  <motion.div
                    key={program.stage}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6"
                  >
                    <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                      <div className={`text-2xl sm:text-3xl text-${program.color}-400`}>
                        {program.icon}
                      </div>
                      <div>
                        <h3 className="text-lg sm:text-xl font-bold text-white">{program.stage}</h3>
                        <p className="text-sm sm:text-base text-blue-200">{program.ageRange}</p>
                      </div>
                    </div>
                    <p className="text-sm sm:text-base text-blue-100 mb-3 sm:mb-4">{program.description}</p>
                    <ul className="space-y-2">
                      {program.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm sm:text-base text-blue-100">
                          <span className="text-blue-400">•</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Education Program Section */}
          <section className="py-8 sm:py-12 md:py-16">
            <div className="max-w-7xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center mb-8 sm:mb-12"
              >
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4">Education Program</h2>
                <p className="text-base sm:text-lg md:text-xl text-blue-100 max-w-2xl mx-auto">
                  Three-basis academy system approach: Full-time, Part-time, and Hybrid options
                </p>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
                {educationPrograms.map((program, index) => (
                  <motion.div
                    key={program.phase}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6"
                  >
                    <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                      <div className={`text-2xl sm:text-3xl text-${program.color}-400`}>
                        {program.icon}
                      </div>
                      <div>
                        <h3 className="text-lg sm:text-xl font-bold text-white">{program.phase}</h3>
                        <p className="text-sm sm:text-base text-blue-200">{program.ageRange}</p>
                      </div>
                    </div>
                    <p className="text-sm sm:text-base text-blue-100 mb-3 sm:mb-4">{program.description}</p>
                    {program.options ? (
                      <div className="space-y-2 sm:space-y-3">
                        {program.options.map((option, i) => (
                          <div key={i} className="bg-white/5 p-2 sm:p-3 rounded-lg">
                            <h4 className="text-sm sm:text-base font-semibold text-white mb-1">{option.title}</h4>
                            <p className="text-xs sm:text-sm text-blue-100">{option.description}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <ul className="space-y-2">
                        {program.features.map((feature, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm sm:text-base text-blue-100">
                            <span className="text-blue-400">•</span>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Additional Programs Section */}
          <section className="py-8 sm:py-12 md:py-16 bg-white/5 backdrop-blur-sm">
            <div className="max-w-7xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center mb-8 sm:mb-12"
              >
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4">Additional Opportunities</h2>
                <p className="text-base sm:text-lg md:text-xl text-blue-100 max-w-2xl mx-auto">
                  Festivals, Tours, and Tournaments
                </p>
              </motion.div>

              <div className="max-w-3xl mx-auto bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 md:p-8">
                <div className="space-y-3 sm:space-y-4 text-sm sm:text-base text-blue-100">
                  <p>
                    Players may have the opportunity to participate in additional festivals, tours, and tournaments, 
                    both domestically and abroad, some of which may be residential.
                  </p>
                  <p>
                    These opportunities are designed to enhance your playing experience and help your technical and personal development.
                  </p>
                  <p>
                    Your Club will provide you with specific details about these events.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-8 sm:py-12 md:py-16 bg-blue-600/20 backdrop-blur-sm">
            <div className="max-w-7xl mx-auto text-center">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4 md:mb-6">Ready to Start Your Journey?</h2>
              <p className="text-base sm:text-lg md:text-xl text-blue-100 mb-6 sm:mb-8">
                Join our comprehensive development program and unlock your potential
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/auth/join')}
                className="bg-white text-blue-600 px-6 sm:px-8 py-2.5 sm:py-3 rounded-full font-semibold text-sm sm:text-base md:text-lg hover:bg-blue-50 transition-colors duration-200"
              >
                Register Now
              </motion.button>
            </div>
          </section>
        </div>

        <Footer />
      </div>
    </div>
  );
};

export default Programs; 